import { Link } from "@novaescola/eva-react-v2/lib/Link";
import { Typography } from "@novaescola/eva-react-v2/lib/Typography";
import axios from "axios";
import { formatInTimeZone } from 'date-fns-tz';
import { Script, graphql } from "gatsby";
import Cookies from "js-cookie";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactGA from "react-ga";

import Layout from "../components/Layout";
import ActionsButtons from "../components/general/ActionsButtons";
import ContentBreadcrumb from "../components/general/ContentBreadcrumb";
import ShareButtons from "../components/general/ShareButtons";
import Tags from "../components/general/Tags";

import { AccountApi } from "../api/account/account.api";
import { meuPerfilApi } from "../api/meu-perfil/meu-perfil.api";
import Article from "../components/content-page/Article";
import BlackAwareness from "../components/content-page/BlackAwareness";
import CarouselRelatedContent from "../components/content-page/CarouselRelatedContent";
import GeneralSkills from "../components/content-page/GeneralSkills";
import { LatestNewsCarousel } from '../components/content-page/LatestNewsCarousel';
import LiteracyInFocus from '../components/content-page/LiteracyInFocus';
import MostRead from "../components/content-page/MostRead";
import Planner from "../components/content-page/Planner";
import SchoolManagement from "../components/content-page/SchoolManagement";
import { TypeFormWidget } from "../components/content-page/TypeForm";
import { getItemsByCategory, isBrowser, isContentActivity, isContentSchoolProjects } from "../data/utils";
import BannerFund2 from "../components/content-page/BannerFund2";
import '../styles/content-page.css';

if (isBrowser()) {
  ReactGA.initialize("UA-26936412-2");
}

export function Head({ data: { content } }) {
  const title = `${content.title} | Nova Escola`;
  const description = content.seoDescription;
  const image = content.thumbnailImage || content.featuredImage;
  
  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={`https://novaescola.org.br${content.url}`} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content="pt_BR" />
      <meta property="og:site_name" content="Nova Escola" />
      <link rel="manifest" href="/manifest.json" />
    </>
  );
}

const ContentPage = ({ data }) => {
  const [isClient, setIsClient] = useState(false)

  useEffect(() => {
    setIsClient(true)
  }, [])

  if (data.content.url === '/conteudo/21305/teste-relacionadas-3') {
    console.log('###### DATA:', data)
  }

  const { tags } = data.content

  let tagsOfContent = tags.map((e) => e.tag);

  useEffect(() => {
    if (isBrowser()) {
      Cookies.set("ane_redirect", window.location.href, {
        domain: ".novaescola.org.br",
      });
    }
  });

  const authKeyName = "aneAuthUser";

  const bearer = Cookies.get('Bearer')

  const [user, setUser] = useState({
    permissions: [],
    type: "anonimo",
    logged: false,
  });
  const [hasAlert, setHasAlert] = useState(false);
  const [showAlert, setShowAlert] = useState(true);
  const [userData, setUserData] = useState(undefined);
  const [loggedIn, setLoggedIn] = useState(false);
  const actionsButtonsProps = useMemo(() => {
    if (userData) {
      const setCookiesToCompleteRegistrationRedirect = (url, contentToSave) => {
        const expires = new Date();
        expires.setTime(expires.getTime() + 60 * 60 * 1000);
        const cookiesOptions = {
          domain: ".novaescola.org.br",
          expires,
          path: "/",
        };
    
        Cookies.set(
          "ane_complemento_cadastro_redirect_url",
          data.content.pdfLink,
          cookiesOptions
        );
    
        Cookies.set(
          "ane_complemento_cadastro_content_to_save_if_print_or_download",
          JSON.stringify(contentToSave),
          cookiesOptions
        );

        return {
          url,
          isPDFLink: false,
        };
      }

      const contentId = data.content.url.split('/')[2];
      const contentTitle = data.content.title;

      const { ocupacao, etapas, disciplinas, raca, enderecos } = userData;
      const dtUltimaModificacao = new Date(userData.dtUltimaModificacao);
      const lastYear = new Date();
      lastYear.setMonth(lastYear.getMonth() - 12);
      const twoMonthsAgo = new Date();
      twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);

      const contentToSave = {
        contentId: parseInt(contentId),
        url: `https://novaescola.org.br${data.content.url}`,
        imageUrl: data.content.thumbnailImage || data.content.featuredImage,
        title: contentTitle,
        category: "",
        description: "Por " + data.content.authors.map(au => au.author).join(', '),
        tag: "Jornalismo",
      };

      const hasAllRequiredData =
        ocupacao &&
        etapas?.length > 0 &&
        disciplinas?.length > 0 &&
        enderecos?.[0]?.estado &&
        enderecos?.[0]?.cidade;

      if (hasAllRequiredData && dtUltimaModificacao > lastYear) {
        return {
          contentId,
          contentTitle,
          url: data.content.pdfLink,
          contentToSave,
          isPDFLink: true,
          complementoCadastroEventLabel:
            "matrícula - botão BUTTON_NAME - matricula completa",
        };
      }

      return setCookiesToCompleteRegistrationRedirect(
        "/matricula",
        contentToSave
      );
    }

    return {
      url: `https://account.novaescola.org.br/auth/signin`,
      isPDFLink: false,
      complementoCadastroEventLabel:
        "matrícula - botão BUTTON_NAME - deslogado fez login ou criou conta",
    };
  }, [userData]);

  const getAuthUser = () =>
    isBrowser() && window.localStorage.getItem(authKeyName)
      ? JSON.parse(window.localStorage.getItem(authKeyName))
      : null;

  const setAuthUser = (user) =>
    window.localStorage.setItem(authKeyName, JSON.stringify(user));

  const fetchUser = async (bearer) => {

    if (!bearer) {
      return null;
    }

    return AccountApi.getProfile(bearer);
  };

  const fetchUserData = async () => {

    const bearer = Cookies.get("Bearer");
    if (bearer) {
      try {
        const response = await AccountApi.getUser(bearer);
        setUserData(response);
      } catch (err) {
        setUserData(null);
      }
    }
  };

  const checkAuthUser = useCallback(async () => {
    let authUser = getAuthUser();

    if (authUser) {
      setUser(authUser);
      setLoggedIn(true);
    }

    try {
      authUser = await fetchUser(bearer);

      if (!authUser) {
        throw new Error("user not found");
      }

      setUser(authUser);
      setAuthUser(authUser);
      setLoggedIn(true);
    } catch (err) {
      setUser({
        permissions: [],
        type: "anonimo",
        logged: false,
      });
      setLoggedIn(false);
      setAuthUser(null);
    }
  }, [bearer]);

  useEffect(() => {
    checkAuthUser();
  }, [checkAuthUser]);

  useEffect(() => {
    if(loggedIn) fetchUserData();
  }, [loggedIn]);

  const publishedAt = new Date(data.content.publishedAt)

  const getUserDataToDataLayer = () => {

		const ocupacaoMap = {
			'PROFESSOR': 0,
			'OUTRA': 1,
			'ESTUDANTE': 2,
			'DIRETOR': 3,
			'COORDENADOR_PEDAGOGICO': 4,
			'P_M_R_POR_ALUNO': 5,
			'SECRETARIO_EDUCACAO': 6,
			'ESTAGIARIO': 7,
			'ORIENTADOR_EDUCACIONAL': 8,
			'AUXILIAR_CLASSE': 9,
			'TECNICO_SECRETARIA': 10,
			'SUB_SECRETARIO_EDUCACAO': 11
		};
		
		const disciplinaMap = {
			'CIENCIAS': 0,
			'FISICA': 1,
			'LINGUA_PORTUGUESA': 2,
			'HISTORIA': 3,
			'FILOSOFIA': 4,
			'SOCIOLOGIA': 5,
			'MATEMATICA': 6,
			'GEOGRAFIA': 7,
			'ARTE': 8,
			'EDUCACAO_FISICA': 9,
			'LINGUA_ESTRANGEIRA': 10,
			'QUIMICA': 11,
			'BIOLOGIA': 12,
			'OUTRO': 13,
			'POLIVALENTE': 14,
			'PROFESSOR_REGENTE': 15,
			'ENSINO_RELIGIOSO': 16
		};
		
		const etapasMap = {
			'ENSINO_MEDIO': 0,
			'ENSINO_FUNDAMENTAL_ANOS_INICIAIS': 1,
			'ENSINO_FUNDAMENTAL_ANOS_FINAIS': 2,
			'EJA': 3,
			'ENSINO_SUPERIOR': 4,
			'EDUCACAO_INFANTIL_PRE_ESCOLA': 5,
			'EDUCACAO_INFANTIL_CRECHE': 6,
			'OUTRO': 7,
			'PRE_VESTIBULAR': 8
		};
		
		const localTrabalhoMap = {
			'OUTRO': 0,
			'REDE_PRIVADA': 1,
			'REDE_PUBLICA_ESTADUAL': 2,
			'REDE_PUBLICA_MUNICIPAL': 3,
			'REDE_PUBLICA_FEDERAL': 4
		};
	
		const moreInfoData = {

			send: async function () {

				window.dataLayer = window.dataLayer || [];
				try {
					const bearer = Cookies.get('Bearer');
					const userData = await AccountApi.getUserData(bearer);
					window.dataLayer.push({
											'event': 'user_data',
											'user_id': userData.id,
											'user_logged': 1,
											'user_name': userData.nome,
											'user_email': userData.email,
											'user_phone': userData.telefone,
											'user_occupation': [ocupacaoMap[userData.ocupacao]],
											'user_discipline': userData.disciplinas.map(function (disciplina) {return disciplinaMap[disciplina.disciplinas]}),
											'user_stage' : userData.etapas.map(function (etapa){ return etapasMap[etapa.etapas] }),
											'user_network': userData.locaisTrabalho.map( function(local){ return localTrabalhoMap[local.localTrabalho]})
									});

				} catch (error) {
					// datalayer sem login
					window.dataLayer.push({
						'event': 'user_data',
						'user_logged': 0
					});
				}
			},
		};
		moreInfoData.send();
	};

  const sendDataLayerViewItem = () => {
    window.dataLayer = window.dataLayer || [];
    const urlArray = (data.content.url ).split('/');
    window.dataLayer.push({
      'event': 'view_item',
      'item_id': urlArray[2],
      'item_name': data.content.title,
      'item_category': 'conteudo',
    });
  };

  const showBlackAwarenessSectionRules = () => {
    const firstOfDecemberTS = new Date('2024-12-01').getTime();
    const todayTS = new Date().getTime();
    if (todayTS < firstOfDecemberTS) {
      return true;
    }
    const categories = data.content.categories.filter(
      (el) =>
        el.category === "DISCIPLINAS" &&
        (el.name === "História" || el.name === "Geografia")
    );
    const blackAwarenessTags = [
      "Antirracismo",
      "Diversidade",
      "Preconceito",
      "Religiões de matriz africana",
      "Abolição da Escravidão",
      "Consciência Negra",
      "Cultura Africana",
      "Escravidão",
      "Escravidão no Brasil",
      "Literatura africana",
      "Pensadores Negros",
      "Racismo",
      "Educação Antirracista",
    ];

    return categories.length > 0 || tagsOfContent.some(el => blackAwarenessTags.includes(el));
  }

	const sendTriggerEscolaDeRespeito = () => {
    const bearer = Cookies.get('Bearer');

		if (!bearer) return;

    const pageUrl = data.content.url;

		if (tagsOfContent.includes("Educação Antirracista")) {
			const viewingDate = new Date();

			axios.post(`${process.env.GATSBY_API_EMAILS_TRANSACIONAIS_URL}/pageView`, {
				token: bearer,
				pageUrl,
				viewingDate,
			});
		}
	}

  const makeThematicRegistration = () => {
    const bearer = Cookies.get("Bearer");

    if (!bearer) return;

    if (tagsOfContent.includes("Educação Antirracista")) {
      meuPerfilApi.makeThematicRegistration(bearer, 1);
    }
  };
  
  const showPlannerSectionRules = () => {
    return tagsOfContent.some(el => el === "Volta às aulas" || el === "Planejamento");
  }

  const showSchoolManagementSectionRules = () => {
    return tagsOfContent.some(
      (el) =>
        el === "Gestão escolar" ||
        el === "Coordenação pedagógica" ||
        el === "Formação de professores"
    );
  }

  const themeBlockToShow = () => {
    const showBlackAwarenessSection = showBlackAwarenessSectionRules();
    const showLiteracyInFocusSection = tagsOfContent.includes("Alfabetização") || tagsOfContent.includes("Alfabetização Matemática");
    const showSchoolManagementSection = showSchoolManagementSectionRules();
    const showFund2 =
      tagsOfContent.includes("Anos finais do Fundamental") ||
      tagsOfContent.includes("Adolescência") ||
      tagsOfContent.includes("Fundamental 2") || 
      getItemsByCategory(data.content.categories, "ETAPAS") === "Anos Finais do Fundamental";

    if (showFund2) return <BannerFund2/>
    else if (showLiteracyInFocusSection) return <LiteracyInFocus />
    else if (showBlackAwarenessSection) return <BlackAwareness />
    else if (showSchoolManagementSection) return <SchoolManagement />
    else if (new Date() <= new Date(2024, 2, 31)) return <GeneralSkills />
  }
  const sendTriggerFund2 = () => {
    const bearer = Cookies.get('Bearer');

		if (!bearer) return;

		if (tagsOfContent.includes("Anos finais do Fundamental") || tagsOfContent.includes("Adolescência")) {
			const viewingDate = new Date();

			axios.post(`${process.env.GATSBY_API_EMAILS_TRANSACIONAIS_URL}/pageView`, {
				token: bearer,
        pageUrl: "/",
				viewingDate,
        typeOfRule: "fund2"
			});
		}
	}
  useEffect(() => {
    getUserDataToDataLayer();
    sendDataLayerViewItem();
    sendTriggerEscolaDeRespeito();
    makeThematicRegistration();
    sendTriggerFund2();
  }, []);

  return isClient && (
    <>
      <Layout
        user={user}
        loggedIn={loggedIn}
        headerClass="padding-header"
        url={data.content.url}
        isOpenContent={data.content.isOpenContent}
        setHasAlert={setHasAlert}
        hasAlert={hasAlert}
        showAlert={showAlert}
        setShowAlert={setShowAlert}
      >
        <div className="r89-desktop-billboard-atf" style={{display: "flex", alignItems: "center", justifyContent: "center"}}></div>
        <div className="r89-mobile-billboard-top" style={{display: "flex", alignItems: "center", justifyContent: "center"}}></div>
        <div className={`container content ${(hasAlert && showAlert) ? "with-alert" : "" }` }>
          <div className="row">
            <section className="eva-desktop-only col-xl-1 col-lg-1 col-md-1 eva-no-padding">
              <div className="fixed-share">
                <Typography variant="body" size={3} asChild className="eva-margin-bottom-1">
                  <p>Compartilhe:</p>
                </Typography>
                <ShareButtons data={data} positionEventLabel="esquerda" />
              </div>
            </section>

            <section className="padding-left col-xl-11 col-lg-11 col-md-12 col-sm-12">

              {data.content.partners.map((partner, index) => (
                <div key={`partner${partner.name + index}`}>
                  <Typography variant="body" size={3} asChild className="eva-margin-bottom-1">
                    <p>{partner.presentationText}</p>
                  </Typography>
                  <div className="partner-image" key={index}>
                    <a href={partner.url} target="_blank" rel="noreferrer">
                      <img
                        src={partner.featuredImage}
                        alt={partner.name}
                        loading="lazy"
                      />
                    </a>
                  </div>
                </div>
              ))}

              <div id="ad-leaderboard1" className="ad-leaderboard1"></div>

              <ContentBreadcrumb data={data} />
              <Typography variant="body" size={2} asChild className="eva-color-inst-500 eva-text-bold eva-margin-bottom-1">
                { isContentActivity(data.content.categories) ? <p>Atividades</p>: isContentSchoolProjects(data.content.categories) ? <p>Projeto escolar</p>:<p>Jornalismo</p>}
              </Typography>

              <div className="row">
                <article className="col-xl-8  col-lg-8  col-md-12 col-sm-12">
                  <Typography variant="title" size={4} asChild className="eva-margin-bottom-3">
                    <h1>{data.content.title}</h1>
                  </Typography>
                  <Typography variant="body" size={1} asChild className="eva-margin-bottom-2">
                   <h2>{data.content.excerpt}</h2>
                  </Typography>
                  <Typography variant="body" size={3} asChild>
                    <p>Por
                      {data.content.authors.map((author, index) => (
                        <Link
                          variant="preto"
                          className="eva-font-14 author-link eva-text-bold"
                          href={author.uri}
                          key={index}
                          data-track="Nova página de conteúdo - link para autor"
                          onClick={() => {
                            ReactGA.event({
                              action: "Click",
                              category: "SITE",
                              label: "Nova página de conteúdo - link para autor",
                            });
                          }}
                        >
                          {author.author}
                        </Link>
                      ))}
                    </p>
                  </Typography>
                  <Typography variant="caption" asChild>
                    <p>{formatInTimeZone(publishedAt, 'America/New_York', 'dd/MM/yyyy')}</p>
                  </Typography>
                </article>

                <aside className="col-xl-4 col-lg-4 col-md-12 col-sm-12">
                  <ActionsButtons {...actionsButtonsProps} />
                  <div className="aside-share">
                    <Typography variant="body" size={2} asChild className="eva-margin-bottom-1">
                      <p>Compartilhe esse conteúdo:</p>
                    </Typography>
                    <ShareButtons data={data} />
                  </div>
                </aside>
                <aside></aside>
              </div>

              <div className="row">
                 <article className="col-xl-8  col-lg-8  col-md-12 col-sm-12">
                  {
                    isContentSchoolProjects(data.content.categories) ?
                  <div className="introducion">
                 <span className="title-introducion">Informações gerais</span>
                  <div className="grid-introducion">
                    <div className="data-introducion">
                      <span className="data-title-introducion">Título da Prática</span>
                      <span>{data.content.title}</span>
                    </div>
                    <div className="data-introducion">
                      <span className="data-title-introducion">Tema</span>
                      <span>{getItemsByCategory(data.content.categories, "TEMAS")}</span>
                    </div>
                    <div className="data-introducion">
                      <span className="data-title-introducion">Etapa do Ensino</span>
                      <span>{getItemsByCategory(data.content.categories, "ETAPAS")}</span>
                    </div>
                    <div className="data-introducion">
                      <span className="data-title-introducion">Ano</span>
                      <span>{getItemsByCategory(data.content.categories, "ANO_LETIVO")}</span>
                    </div>
                    <div className="data-introducion">
                      <span className="data-title-introducion">Componentes Curriculares</span>
                      <span>{getItemsByCategory(data.content.categories, "DISCIPLINAS")}</span>
                    </div>
                  </div>
                  </div>
                    : null
                  }

                  <Article data={data} loggedIn={loggedIn} userData={userData} />

                  <TypeFormWidget user={userData} />

                  <Tags data={data} />
                </article>

                <aside className="aside col-xl-4 col-lg-4 col-md-3 col-sm-12 d-flex fd-column">
                  {themeBlockToShow()}
                  <MostRead allContentMostViewed={data.allContentMostViewed} />
                  <div className="r89-desktop-hpa-atf eva-desktop-only" />
                  <div></div>
                  <div id="ad-rectangle2" className="ad-rectangle2"> </div>
                </aside>
              </div>
            </section>
          </div>
        </div>
        {data.content.related.length > 0 && (
          <section className="container-fluid relacionadas eva-bg-cinza-50">
            <div className="container">
              <CarouselRelatedContent contents={data.content.related} />
            </div>
          </section>
        )}
        <section className="container-fluid latest-news">
          <div className="container">
            <LatestNewsCarousel allContentNewest={data.allContentNewest} />
          </div>
        </section>
      </Layout>

      <Script src="//estaticos.novaescola.org.br/header-footer/ads.js" />
    </>
  );
};

export const query = graphql`
  query ($id: String) {
    content(id: { eq: $id }) {
      categories {
        category
        name
      }
      title
      featuredImage
      thumbnailImage
      isOpenContent
      excerpt
      publishedAt
      url
      seoDescription
      authors {
        id
        author
        uri
      }
      content
      tags {
        id
        tag
        uri
      }
      pdfLink
      related {
        id
        title
        url
        featuredImage
        external
      }
      partners {
        presentationText
        name
        url
        featuredImage
      }
    }
    allContentMostViewed(sort: { rankPosition: ASC }) {
      nodes {
        path
        rankPosition
        title
      }
    }
    allContentNewest {
      nodes {
        header
        featuredImage
        excerpt
        title
        url
      }
    }
  }
`;

export default ContentPage;
